import { Component, DestroyRef, DOCUMENT, Inject, inject, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { BillingDetailsStatus, BillingDetailsStatusRaw, BillingNotificationService } from './billing-notification.service';
import { combineLatest } from 'rxjs';
import { UserService } from '../shared/services/user.service';

import { GtmService } from '../shared/gtm/gtm.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { GtmModule } from '../shared/gtm/gtm.module';
import { CwComponentsModule } from '../shared/components/components.module';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';

@Component({
    selector: 'zp-billing-notification',
    styleUrls: [
        './billing-notification.component.scss'
    ],
    templateUrl: './billing-notification.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        RouterModule,
        CommonModule,
        GtmModule,
        CwComponentsModule,
        DialogModule,
        ButtonModule
    ],
    providers: [
        BillingNotificationService
    ]
})
export class BillingNotificationComponent implements OnInit {

    public isSoftWarning = false;
    public isHardWarning = false;
    public isSoftWarningBillingData = false;

    private readonly BODY_ALERT_CLASS = 'zp-billing-notifier__alert--enabled';
    private readonly ANIMATION_DELAY = 2000;
    private bodyRef: HTMLElement;

    private destroyRef = inject(DestroyRef);

    constructor(
        private billingNotificationService: BillingNotificationService,
        private userService: UserService,
        private renderer: Renderer2,
        private gtmService: GtmService,
        @Inject(DOCUMENT) document: Document,
        private router: Router
    ) {
        this.bodyRef = document.body;
    }

    public ngOnInit(): void {
        this.billingNotificationService.fetchBillingDetailsStatus();
        this.updateBillingStatus();
    }

    /* This should handle subscription for:
            this.pubSubFacade.subscribeOnTopic(
               PUB_SUB_TOPICS.BILLING_UPDATE,
               () => this.updateBillingStatus()
           );*/
    public updateBillingStatus(): void {
        combineLatest([this.billingNotificationService.billingStatus$, this.userService.user$])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                ([billingDetailsStatus, { billingDataVerificationStatus, publisherDetails: { userInfo } }]) => {
                    this.onStatusesReady(billingDataVerificationStatus, billingDetailsStatus, userInfo.clientId);
                }
            );
    }

    public onStatusesReady(billingVerificationStatus: string, billingDetailsStatus: BillingDetailsStatusRaw, clientId: string): void {
        const isFailed = 0 === (billingVerificationStatus || '').indexOf('FAILED');
        const billingStatus: BillingDetailsStatus = isFailed ? BillingDetailsStatus.INVALID_BILLING : billingDetailsStatus;
        const closedBillingStatus = this.billingNotificationService.getCloseAlertState(clientId);
        let gtmStatus = 'valid';

        if (closedBillingStatus === billingStatus) {
            return;
        }

        this.billingNotificationService.clearCloseAlertState(clientId);
        switch (billingStatus) {
            case BillingDetailsStatus.SOFT:
                gtmStatus = 'soft-warning';
                setTimeout(() => this.showSoftWarning(), this.ANIMATION_DELAY);
                break;
            case BillingDetailsStatus.HARD:
                gtmStatus = 'hard-warning';
                setTimeout(() => this.showHardWarning(), this.ANIMATION_DELAY);
                break;
            case BillingDetailsStatus.INVALID_BILLING:
                gtmStatus = 'invalid-data';
                setTimeout(() => this.showSoftWarningBillingData(), this.ANIMATION_DELAY);
                break;
            default:
                this.onCloseSoftWarning();
                this.onCloseSoftWarningBillingData();
                this.onCloseHardWarning();
        }

        this.gtmService.dataLayerTopic$.next(
            {
                event: 'ShowAlert',
                billingDataStatus: gtmStatus
            }
        );
    }

    public showSoftWarning(): void {
        this.isSoftWarning = true;
        this.renderer.addClass(this.bodyRef, this.BODY_ALERT_CLASS);
    }

    public showSoftWarningBillingData(): void {
        this.isSoftWarningBillingData = true;
        this.renderer.addClass(this.bodyRef, this.BODY_ALERT_CLASS);
    }

    public showHardWarning(): void {
        this.isHardWarning = true;
    }

    public onCloseSoftWarning(): void {
        this.isSoftWarning = false;
        this.renderer.removeClass(this.bodyRef, this.BODY_ALERT_CLASS);
        this.userService.clientId$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(clientId => this.billingNotificationService.saveCloseAlertState(BillingDetailsStatus.SOFT, clientId));
    }

    public onCloseSoftWarningBillingData(): void {
        this.isSoftWarningBillingData = false;
        this.renderer.removeClass(this.bodyRef, this.BODY_ALERT_CLASS);
        this.userService.clientId$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(clientId => this.billingNotificationService.saveCloseAlertState(BillingDetailsStatus.INVALID_BILLING, clientId));
    }

    public goToBillingDetails(): void {
        this.router.navigate(['/account/billing-info']);
    }

    public onCloseHardWarning(): void {
        this.isHardWarning = false;
        this.userService.clientId$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(clientId => this.billingNotificationService.saveCloseAlertState(BillingDetailsStatus.HARD, clientId));
    }
}
